// 比較の選択状態（一覧のチェック＋画面下部の選択バー）。
//
// 選択は localStorage aicon:compare:v1 に [{handle, title, image, model_no}] で持つ
// （比較表ページ assets/aicon-compare-table.js と同じキーを読む）。上限3機種。
//
// <aicon-compare-toggle>  … 商品カードの「比較する」チェック。infinite scroll で
//                           後から追加されたカードにも connectedCallback で状態が復元される。
// <aicon-compare-bar>     … 画面下部固定の選択バー。0件は非表示・1件はCTA無効・
//                           3件でチェック側を打ち止めにする。
//
// 商品詳細の「このエアコンと別のエアコンを比較する」導線は
// 一覧URLに ?compare-add=handle を付けて遷移してくる。バーが検出して選択に加える。

const STORAGE_KEY = 'aicon:compare:v1';
// 上限は sections/aicon-compare-table.liquid の data-max（比較表側）と必ず揃えること
const MAX_MODELS = 3;
const CHANGE_EVENT = 'aicon:compare:change';
const COLUMN_SECTION = 'aicon-compare-column';

// localStorage が使えない環境（プライベートモード・ストレージブロック等）向けの
// インメモリ・フォールバック。書き込みに一度でも失敗したらこちらを真とする。
// ページ内の選択操作は成立し、リロードで消えるだけになる。
let memorySelection = null;

const readSelection = () => {
  if (memorySelection !== null) return memorySelection;
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.filter((item) => item && item.handle) : [];
  } catch {
    return [];
  }
};

const writeSelection = (items) => {
  const next = items.slice(0, MAX_MODELS);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    if (memorySelection !== null) memorySelection = next;
  } catch {
    memorySelection = next;
  }
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT));
};

const isSelected = (handle) => readSelection().some((item) => item.handle === handle);

const addSelection = (item) => {
  const items = readSelection();
  if (items.some((existing) => existing.handle === item.handle)) return true;
  if (items.length >= MAX_MODELS) return false;
  writeSelection([...items, item]);
  return true;
};

const removeSelection = (handle) => {
  writeSelection(readSelection().filter((item) => item.handle !== handle));
};

// 別タブでの変更にも追従する
window.addEventListener('storage', (event) => {
  if (event.key === STORAGE_KEY) document.dispatchEvent(new CustomEvent(CHANGE_EVENT));
});

class AiconCompareToggle extends HTMLElement {
  connectedCallback() {
    if (!this.button) {
      this.button = document.createElement('button');
      this.button.type = 'button';
      this.button.className = 'aicon-compare-toggle';
      this.box = document.createElement('span');
      this.box.className = 'aicon-compare-toggle__box';
      this.box.setAttribute('aria-hidden', 'true');
      this.label = document.createElement('span');
      this.label.className = 'aicon-compare-toggle__label';
      this.label.textContent = this.dataset.label || '比較する';
      this.button.append(this.box, this.label);
      // カード全体が商品詳細へのリンクのため、チェック操作は遷移させない
      this.button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.toggle();
      });
      this.appendChild(this.button);
    }
    this.update = this.update.bind(this);
    document.addEventListener(CHANGE_EVENT, this.update);
    this.update();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.update);
  }

  item() {
    return {
      handle: this.dataset.handle,
      title: this.dataset.title || '',
      image: this.dataset.image || '',
      model_no: this.dataset.modelNo || '',
    };
  }

  toggle() {
    if (isSelected(this.dataset.handle)) {
      removeSelection(this.dataset.handle);
    } else if (!addSelection(this.item())) {
      // 上限。バー側の文言（比較は3件まで）に気づかせる
      document.dispatchEvent(new CustomEvent(CHANGE_EVENT));
    }
  }

  update() {
    const selected = isSelected(this.dataset.handle);
    const full = !selected && readSelection().length >= MAX_MODELS;
    this.button.classList.toggle('aicon-compare-toggle--checked', selected);
    this.button.classList.toggle('aicon-compare-toggle--disabled', full);
    this.button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    this.box.textContent = selected ? '✓' : '';
  }
}

class AiconCompareBar extends HTMLElement {
  connectedCallback() {
    this.compareUrl = this.dataset.compareUrl || '/pages/compare';
    this.refs = {};
    for (const el of this.querySelectorAll('[data-ref]')) {
      this.refs[el.dataset.ref] = el;
    }
    this.refs.clear?.addEventListener('click', () => writeSelection([]));
    this.update = this.update.bind(this);
    document.addEventListener(CHANGE_EVENT, this.update);
    this.consumeCompareAdd();
    this.update();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.update);
  }

  // 商品詳細からの導線: ?compare-add=handle を選択に加え、URLから取り除く
  // （畳数フィルタ等の他のクエリは残す）。カード情報は後追いで補完する。
  async consumeCompareAdd() {
    const url = new URL(location.href);
    const handle = url.searchParams.get('compare-add');
    if (!handle) return;
    url.searchParams.delete('compare-add');
    history.replaceState(null, '', url);
    if (isSelected(handle)) return;
    if (!addSelection({ handle, title: '', image: '', model_no: '' })) return;
    try {
      const res = await fetch(`/products/${encodeURIComponent(handle)}?sections=${COLUMN_SECTION}`);
      if (!res.ok) throw new Error(String(res.status));
      const html = (await res.json())[COLUMN_SECTION] || '';
      const payload = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('[data-aicon-compare-product]');
      if (!payload) return;
      const column = JSON.parse(payload.textContent);
      writeSelection(
        readSelection().map((item) =>
          item.handle === handle
            ? { handle, title: column.title || '', image: column.image || '', model_no: column.model_no || '' }
            : item
        )
      );
    } catch {
      // 補完できなくても handle は選択済みなので比較表は成立する
    }
  }

  /**
   * AI の提案が今の画面に載っているか。
   *
   * ★ 提案があるなら1件でも比較が成立する（日高さん指摘 2026-08-28）★
   *   提案は URL の `?ai=<handle,...>&aiq=<quoteId>` だけが出どころで（保存は持たない
   *   ＝ 別の診断の提案が復活するのを防ぐため）、比較ページはこの2つが**揃ったときだけ**
   *   AI 列を描く。つまり両方あるなら、お客様が選んだ1件と AI の提案が並ぶので、
   *   自分で2件選ぶ必要はない。ここを 2 件固定にしていたため、
   *   **提案付きで来た人が「比較する」を押せなかった**。
   *
   *   判定は `buildCompareHref` と同じ条件（`ai` と `aiq` の両方）にすること。
   *   片方だけで通すと、比較ページが AI 列を描かず**1列だけの表**になる。
   */
  hasAiPicks() {
    const here = new URLSearchParams(location.search);
    return Boolean((here.get('ai') || '').trim() && (here.get('aiq') || '').trim());
  }

  update() {
    const items = readSelection();
    this.hidden = items.length === 0;
    if (items.length === 0) return;

    // AI の提案があれば1件で足りる。無ければ従来どおり2件から
    const minModels = this.hasAiPicks() ? 1 : 2;

    this.refs.count.textContent = `${items.length}件選択中`;
    this.refs.hint.textContent =
      items.length < minModels
        ? `${minModels}件から比較できます`
        : items.length >= MAX_MODELS
          ? '比較は3件までです'
          : items.length === 1
            ? 'AIの提案と並べて比較できます'
            : 'あと1件まで追加できます';

    // サムネイル（確定デザイン 103:3852: 各サムネの右上に×削除ボタン）
    this.refs.thumbs.textContent = '';
    for (const item of items) {
      const thumb = document.createElement('span');
      thumb.className = 'aicon-compare-bar__thumb';
      if (item.image) {
        const img = document.createElement('img');
        img.src = item.image;
        img.alt = item.title;
        img.loading = 'lazy';
        thumb.appendChild(img);
      } else {
        thumb.append((item.title || item.handle || '?').slice(0, 1));
      }
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'aicon-compare-bar__thumb-remove';
      remove.setAttribute('aria-label', `${item.title || item.handle}を比較から外す`);
      remove.textContent = '×';
      remove.addEventListener('click', () => removeSelection(item.handle));
      thumb.appendChild(remove);
      this.refs.thumbs.appendChild(thumb);
    }

    const ready = items.length >= minModels;
    this.refs.cta.classList.toggle('aicon-compare-bar__cta--disabled', !ready);
    this.refs.cta.setAttribute('aria-disabled', ready ? 'false' : 'true');
    this.refs.cta.href = ready ? this.buildCompareHref(items) : '#';
  }

  /**
   * 比較ページへのURLを組む。
   *
   * ★ AI の提案（?ai=&?aiq=）を落とさない（日高さん指摘 2026-08-21）★
   * AI の提案は URL だけが出どころで、保存は持たない（ゾンビの温床になるため）。
   * ここで products= だけを組んでいたため、比較ページ →「機種を追加する」→ 一覧 →
   * 比較バー → 比較ページ と往復すると AI 列が消えていた。
   *
   * 比較ページ側が「機種を追加する」のリンクに ?ai=&?aiq= を載せてくれるので、
   * ここでは**いま自分がいるURLから拾って載せ直す**だけでよい。保存は増やさない。
   */
  buildCompareHref(items) {
    const url = new URL(this.compareUrl, location.origin);
    url.searchParams.set('products', items.map((item) => item.handle).join(','));

    const here = new URLSearchParams(location.search);
    const ai = (here.get('ai') || '').trim();
    const aiq = (here.get('aiq') || '').trim();
    // 片方だけでは比較ページが読まない（quoteId 一致を必須にしてある）。両方そろって初めて載せる
    if (ai && aiq) {
      url.searchParams.set('ai', ai);
      url.searchParams.set('aiq', aiq);
    }
    return `${url.pathname}${url.search}`;
  }
}

if (!customElements.get('aicon-compare-toggle')) {
  customElements.define('aicon-compare-toggle', AiconCompareToggle);
}
if (!customElements.get('aicon-compare-bar')) {
  customElements.define('aicon-compare-bar', AiconCompareBar);
}
